import { useEffect, useState } from 'react'
import requestLoyaltyClaimHistory from 'api/loyalty/requestLoyaltyClaimHistory'
import { useAppSelector } from 'hooks/reduxHooks'

// Matches the backend's count of successful claims (which limits claims per member)
const SUCCESSFUL_CLAIM_STATUSES = new Set(['COMPLETED', 'PARTIAL_FAILED'])

// Per member, for this page load: the campaigns they've successfully claimed
const claimedCampaignIdsByMember = new Map<string, Promise<Set<string>>>()

/** The campaigns this member has successfully claimed, loaded once per page load. */
export function loadClaimedCampaignIds(memberId: string): Promise<Set<string>> {
  let claimed = claimedCampaignIdsByMember.get(memberId)
  if (!claimed) {
    claimed = requestLoyaltyClaimHistory()
      .then(claims => new Set(claims.filter(claim => SUCCESSFUL_CLAIM_STATUSES.has(claim.status)).map(claim => claim.campaignId)))
      .catch((err) => {
        // Let a later caller retry
        claimedCampaignIdsByMember.delete(memberId)
        throw err
      })
    claimedCampaignIdsByMember.set(memberId, claimed)
  }
  return claimed
}

/** Records a claim made (or found to already exist) this page load, without reloading the history. */
export function markCampaignClaimed(memberId: string | undefined, campaignId: string): void {
  if (!memberId) return
  const previous = claimedCampaignIdsByMember.get(memberId) ?? Promise.resolve(new Set<string>())
  claimedCampaignIdsByMember.set(
    memberId,
    previous.catch(() => new Set<string>()).then(claimed => new Set([...claimed, campaignId])),
  )
}

/** Clears the cache (for tests). */
export function resetClaimHistoryCache(): void {
  claimedCampaignIdsByMember.clear()
}

/**
 * The campaigns the logged-in member has successfully claimed, or undefined while loading (or when
 * not `enabled`). If the history can't be loaded this falls back to an empty set: a repeat claim is
 * then rejected by the backend with a 409, which the claim flows treat as already claimed.
 */
export function useClaimedTierUpgradeCampaignIds(enabled: boolean): Set<string> | undefined {
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const [loaded, setLoaded] = useState<{ memberId: string, claimed: Set<string> }>()

  useEffect(() => {
    if (!enabled || !memberId) return
    let cancelled = false
    loadClaimedCampaignIds(memberId)
      .then(claimed => { if (!cancelled) setLoaded({ memberId, claimed }) })
      .catch(() => { if (!cancelled) setLoaded({ memberId, claimed: new Set() }) })
    return () => { cancelled = true }
  }, [enabled, memberId])

  return enabled && loaded && loaded.memberId === memberId ? loaded.claimed : undefined
}
